// The forecast maths, shared by the landing calculator and the plan page's
// simulation. Mirrors app/models/projection.rb exactly — a fixed monthly
// contribution compounding at the split-weighted historical band — so every
// preview matches what a real plan projects.
//
// `denom` is what the split's weights sum to: 100 for preset percentages,
// the live slider sum when the user is mid-edit.
export function projectRange({ amount, months, bands, split, denom }) {
  const contributed = amount * months
  const values = {}
  for (const bound of ["low", "high"]) {
    let annual = 0
    for (const bag of ["gold", "stocks", "crypto"]) {
      annual += bands[bag][bound] * (split[bag] / denom)
    }
    const monthly = Math.pow(1 + annual, 1 / 12) - 1
    values[bound] = monthly === 0
      ? contributed
      : amount * ((Math.pow(1 + monthly, months) - 1) / monthly)
  }
  // The headline is the middle of the range, never the best case.
  return { low: values.low, high: values.high, mid: (values.low + values.high) / 2, contributed }
}

// Rounded to clean human numbers, so a hypothetical never looks falsely
// precise: $461,111 -> "$460k", $13,735 -> "$14k", $1,240,000 -> "$1.2M".
// Shared by the landing calculator and /dca-calculator; the server renders
// the same figures with DcaCalculatorsHelper#rounded_dollars.
export function roundedDollars(value) {
  if (value < 0) return `-${roundedDollars(-value)}`
  if (value >= 1_000_000) {
    const m = value / 1_000_000
    return `$${(m >= 10 ? Math.round(m) : Number(m.toFixed(1)))}M`
  }
  if (value >= 100_000) return `$${Math.round(value / 10_000) * 10}k`
  if (value >= 10_000) return `$${Math.round(value / 1_000)}k`
  if (value >= 1_000) return `$${Number((value / 1_000).toFixed(1))}k`
  return `$${Math.round(value / 10) * 10}`
}

// Exact dollars for the amounts the user typed ("$100", "$1,000").
export function dollars(value) {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
}

// The same, keeping cents when there are some ("$50", "$50.50"): the plan
// builder reads the typed amount back on every keystroke, so one formatter.
const exactFormat = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 })
export function exactDollars(value) {
  return exactFormat.format(value)
}
